import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ApiError,
  api,
  can,
  capabilityAct,
  capabilityCatalogue,
  holdersOf,
  loadTiers,
  refusalReason,
  roleNames,
  rolesWith,
  shortDate,
  signedInRoleCodes,
  tierLabel,
  tierTableLoaded,
  type ApprovalMatrixVersion,
  type ApprovalStage,
  type Capability,
  type GateConditionEntry,
  type MatrixDocument,
  type MatrixPromoteResult,
  type MatrixRow,
  type TierRow,
} from '../lib/api'
import {
  RELEASE_RULE_HEADLINE,
  RELEASE_RULE_INTRO,
  RELEASE_RULE_LINES,
  RELEASE_RULE_SAFEGUARDS,
  releaseDraft,
  releaseStep,
  rowFor,
  type ReleaseStep,
  withAcceptance,
  withStageCapability,
  withStages,
  withTier,
} from '../lib/matrixDraft'
import { BandChip } from '../components/BandChip'
import { ErrorBox, Loading } from '../components/DataTable'
import { Why, useToast } from '../components/ui'
import { ExitChainEditor, exitProblemCount } from '../components/ExitChainEditor'
import { Dialog, PageTabs, Status, Toggletip, Picker, type PageTab, type Tone } from '../components/kit'
import { ReasonTip } from '../components/vendor/ReasonTip'
import type { ExitRowDoc, ExitVocabulary } from '../lib/exit'
import '../styles/studio.css'
import '../styles/sweep.css'

// Who has to sign a vendor into the register, and what has to be true first.
//
// This is the risk model studio's shape rather than a second way of editing
// versioned tenant content: a list of versions, one of them open, a draft that
// is saved and checked, and a promotion that retires what it replaces and
// stamps a content hash. Somebody who has published a questionnaire or a risk
// model already knows how to work this screen, and a second pattern would be a
// second set of rules for a reviewer to learn.
//
// The document holds two answers. Rows say who signs for a band and tier, in
// order, each stage by a different person. Conditions say which of the named
// gate conditions are in force at each tier, because a stationery supplier and
// a core banking processor do not need the same gate. Both are versioned for
// the same reason: a change to who must approve a critical supplier is a
// control change a reviewer should be able to read and agree to, not a
// deployment.
//
// The three capabilities are not one. Reading is config.read, drafting and
// saving are programme.write, and promotion carries vendor.approve, because
// changing who has to sign a supplier in is a decision of the same kind as
// signing one in. Each control here is greyed by the capability the route it
// calls actually declares.

// A version's state as a label. Retired is finished, so it goes quiet; a code
// the console does not know still shows, in words rather than capitals.
const VERSION_STATUS: Record<string, { tone: Tone; label: string }> = {
  DRAFT: { tone: 'info', label: 'Draft' },
  ACTIVE: { tone: 'ok', label: 'Active' },
  RETIRED: { tone: 'plain', label: 'Retired' },
}

function VersionStatus({ status }: { status: string }) {
  const known = VERSION_STATUS[status]
  if (known) return <Status tone={known.tone}>{known.label}</Status>
  const words = status.toLowerCase().replace(/_/g, ' ')
  return <Status tone="neutral">{words.charAt(0).toUpperCase() + words.slice(1)}</Status>
}

// The two conditions domain.Matrix.Validate refuses a tier for dropping. The
// server is the authority and refuses the promotion; this is the same refusal
// said at the field, so nobody unchecks a box, saves, and only then learns the
// document can never be promoted. The field says that much in one line and the
// reason is one press away.
const ESSENTIAL: Record<string, { line: string; tip: string }> = {
  TIER_RESOLVED: {
    line: 'Required, or this version can never be promoted',
    tip: 'The gate looks the chain up by tier, so a tier that stops requiring one has nothing to evaluate against.',
  },
  APPROVALS_SIGNED: {
    line: 'Required, or this version can never be promoted',
    tip: 'Without this the matrix is a document nothing reads: the signatures would be collected and never required.',
  },
}

// One paragraph carries the reason the authoring controls are dead, and each of
// them points at it. Repeating the sentence beside three buttons would be three
// places to keep in step.
const AUTHOR_REFUSAL_ID = 'matrix-author-refusal'

// What a stage added by hand starts as. The catalogue is ordered reads first,
// so its first row is session.read, which every role in the table holds: a
// stage that defaulted to it was a signature anybody signed in could give, and
// nothing between here and the database objects to one once a reason is typed.
// Starting at the decision the whole chain exists for makes a weaker stage a
// change somebody made deliberately. It stays vendor.approve even where the
// role table holds nobody who can sign it, because the chain then says so
// under the field, in the words the promotion would be refused with.
const NEW_STAGE: Capability = 'vendor.approve'

function asApiError(err: unknown): ApiError {
  return err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err))
}

// The document's edits (rowFor, withAcceptance, withStages, withTier and
// withStageCapability) live in lib/matrixDraft.ts, where a test holds that
// every one of them keeps the fields this screen does not edit: a stage's
// roles and the matrix's contract waiver.

// What is wrong with one chain, in the words the field shows. The capability
// checks are skipped when the role catalogue did not load, because an empty
// answer there means unknown rather than nobody, and marking every stage as
// unholdable would be a screen full of red about a request that never happened.
function chainProblems(row: MatrixRow | undefined): string[] {
  if (!row || row.stages.length === 0) {
    return ['Nobody signs here, so a vendor would enter the register with no signature']
  }
  const catalogueKnown = capabilityCatalogue().length > 0
  const problems: string[] = []
  const seen = new Set<string>()
  row.stages.forEach((s, i) => {
    const at = 'Signature ' + (i + 1)
    if (seen.has(s.capability)) {
      problems.push(at + ' asks for ' + s.capability + ' again, so one person holding only that could sign both')
    }
    seen.add(s.capability)
    if (catalogueKnown && rolesWith(s.capability).length === 0) {
      problems.push('No role holds ' + s.capability + ', so ' + at.toLowerCase() + ' could never be given')
    }
    if (s.why.trim() === '') problems.push(at + ' does not say what it is for')
  })
  return problems
}

// What the last check said, and which check it was. A person needs to know
// whether they are reading the stored document or the one they just saved.
type Checked = { valid: boolean; problems: string[]; of: string }

export function ApprovalMatrix() {
  const [versions, setVersions] = useState<ApprovalMatrixVersion[] | null>(null)
  const [vocabulary, setVocabulary] = useState<GateConditionEntry[]>([])
  const [bands, setBands] = useState<string[]>([])
  const [exitVocabulary, setExitVocabulary] = useState<ExitVocabulary>({
    exitConditions: [],
    exitAudiences: [],
    defaultExit: [],
  })
  const [error, setError] = useState<unknown>(null)

  const [openId, setOpenId] = useState<string | null>(null)
  const [version, setVersion] = useState<ApprovalMatrixVersion | null>(null)
  // Kept apart from the error above. That one means the version list never
  // arrived and there is nothing to work with; this one means one version
  // could not be read, and the list of versions is exactly the control
  // somebody needs to try another one or the same one again.
  const [versionError, setVersionError] = useState<ApiError | null>(null)
  const [draft, setDraft] = useState<MatrixDocument | null>(null)
  // One tier on the page at a time (10 Oct 2026). All four, each with its four
  // chains, and the exit chain under them, was twenty screens: the tier being
  // worked on was somewhere in the middle of it. Empty means the first tier.
  const [part, setPart] = useState('')
  const [dirty, setDirty] = useState(false)
  const [checked, setChecked] = useState<Checked | null>(null)
  const [checking, setChecking] = useState(false)

  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<ApiError | null>(null)
  const [savedNote, setSavedNote] = useState<string | null>(null)

  const [drafting, setDrafting] = useState(false)
  const [draftError, setDraftError] = useState<ApiError | null>(null)

  const [promoting, setPromoting] = useState(false)
  const [promoteError, setPromoteError] = useState<ApiError | null>(null)
  const [promoted, setPromoted] = useState<MatrixPromoteResult | null>(null)

  const [tiers, setTiers] = useState<TierRow[]>(() => tierTableLoaded() ?? [])
  const [subject, setSubject] = useState('')
  // Who changed the open version after it was written. None of them may put
  // it in force, which the promotion panel says before the button is pressed.
  const [openEditedBy, setOpenEditedBy] = useState<string[]>([])

  // The release's draft, when the upgrade added one: who changed it, the
  // proposal or confirmation in flight, and what the last one said.
  const [releaseEditedBy, setReleaseEditedBy] = useState<string[]>([])
  const [releaseBusy, setReleaseBusy] = useState(false)
  const [releaseError, setReleaseError] = useState<ApiError | null>(null)
  const [releaseNote, setReleaseNote] = useState<string | null>(null)
  const toast = useToast()

  const load = useCallback(() => {
    api
      .approvalMatrixVersions()
      .then((r) => {
        setVersions(r.versions)
        setVocabulary(r.conditions ?? [])
        setBands(r.bands ?? [])
        const withExit = r as typeof r & Partial<ExitVocabulary>
        setExitVocabulary({
          exitConditions: withExit.exitConditions ?? [],
          exitAudiences: withExit.exitAudiences ?? [],
          defaultExit: withExit.defaultExit ?? [],
        })
        setError(null)
        setOpenId((prev) => prev ?? r.versions.find((v) => v.status === 'ACTIVE')?.id ?? r.versions[0]?.id ?? null)
      })
      .catch(setError)
  }, [])

  useEffect(load, [load])

  useEffect(() => {
    let live = true
    loadTiers()
      .then((rows) => {
        if (live) setTiers(rows)
      })
      .catch(() => undefined)
    // Who is signed in, for the half of promotion that is not a capability:
    // whoever drafted a version may not be the one who puts it in service.
    api
      .me()
      .then((m) => {
        if (live) setSubject(m.subject)
      })
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [])

  const openVersion = useCallback(
    (id: string) => {
      setChecking(true)
      api
        .approvalMatrixVersion(id)
        .then((v) => {
          setVersion(v.version)
          setOpenEditedBy(v.editedBy ?? [])
          setDraft(v.version.matrix ?? { rows: [], conditions: [] })
          setChecked({ valid: v.valid, problems: v.problems, of: 'the stored version' })
          setDirty(false)
          setSavedNote(null)
          setVersionError(null)
        })
        .catch((err) => setVersionError(asApiError(err)))
        .finally(() => setChecking(false))
    },
    [],
  )

  useEffect(() => {
    setVersionError(null)
    if (!openId) {
      setVersion(null)
      setDraft(null)
      setChecked(null)
      return
    }
    setVersion(null)
    setDraft(null)
    setChecked(null)
    openVersion(openId)
  }, [openId, openVersion])

  function save() {
    if (!openId || !draft) return
    setSaving(true)
    setSaveError(null)
    api
      .saveApprovalMatrixVersion(openId, draft)
      .then((r) => {
        setSavedNote(r.note ?? 'Saved.')
        setDirty(false)
        setChecked({ valid: r.valid, problems: r.problems, of: 'what was just saved' })
        // A save is an edit: the saver joins the people who may not put this
        // draft in force, and a change to a proposed draft withdrew the
        // proposal. Both are read back rather than guessed.
        load()
        api
          .approvalMatrixVersion(openId)
          .then((v) => {
            setVersion(v.version)
            setOpenEditedBy(v.editedBy ?? [])
          })
          .catch(() => undefined)
      })
      .catch((err) => setSaveError(asApiError(err)))
      .finally(() => setSaving(false))
  }

  function newDraft(fromVersionId?: string) {
    setDrafting(true)
    setDraftError(null)
    setPromoted(null)
    api
      .createApprovalMatrixVersion(fromVersionId ? { fromVersionId } : {})
      .then((r) => {
        setOpenId(r.id)
        load()
      })
      .catch((err) => setDraftError(asApiError(err)))
      .finally(() => setDrafting(false))
  }

  function promote() {
    if (!openId) return
    setPromoting(true)
    setPromoteError(null)
    api
      .promoteApprovalMatrixVersion(openId)
      .then((r) => {
        setPromoted(r)
        load()
        openVersion(openId)
      })
      .catch((err) => setPromoteError(asApiError(err)))
      .finally(() => setPromoting(false))
  }

  // The release's draft and who changed it, read whichever version is open.
  const release = versions ? releaseDraft(versions) : undefined
  const releaseId = release?.id ?? null
  useEffect(() => {
    let live = true
    if (!releaseId) {
      setReleaseEditedBy([])
      return
    }
    api
      .approvalMatrixVersion(releaseId)
      .then((v) => {
        if (live) setReleaseEditedBy(v.editedBy ?? [])
      })
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [releaseId, versions])

  // The two acts on the release's draft go to the same route as a promotion:
  // the server records the TPRM Admin's proposal (202, nothing in force
  // changes) or a second person's confirmation (the rules go into force).
  function releaseAct(id: string) {
    setReleaseBusy(true)
    setReleaseError(null)
    setReleaseNote(null)
    api
      .promoteApprovalMatrixVersion(id)
      .then((r) => {
        if (r.status === 'ACTIVE') {
          toast('The new approval rules are in force.')
          setPromoted(r)
        } else {
          toast('Activation proposed. Nothing has changed yet; a second person confirms it.')
        }
        setReleaseNote(r.note ?? null)
        load()
        if (openId) openVersion(openId)
      })
      .catch((err) => setReleaseError(asApiError(err)))
      .finally(() => setReleaseBusy(false))
  }

  // Heaviest tier first. The matrix is read to answer "who signs off a critical
  // supplier", and that row should not be at the bottom of four sections.
  const ordered = useMemo(() => [...tiers].sort((a, b) => a.displayRank - b.displayRank), [tiers])
  const openPart = part || (ordered[0] ? 'tier-' + ordered[0].tier : 'exit')

  if (error) return <ErrorBox error={error} what="The approval matrix" />
  if (!versions) return <Loading what="the approval matrix" />

  const active = versions.find((v) => v.status === 'ACTIVE')
  const editable = version?.status === 'DRAFT' && can('programme.write')
  const drafted = version != null && subject !== '' && version.createdBy === subject
  const edited = subject !== '' && openEditedBy.some((e) => e.toLowerCase() === subject.toLowerCase())

  function set(next: MatrixDocument) {
    setDraft(next)
    setDirty(true)
    setSavedNote(null)
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Approval matrix</h1>
        </div>
        <div className="studio-header-actions">
          <button
            type="button"
            className="primary"
            disabled={drafting || !can('programme.write')}
            title={can('programme.write') ? undefined : refusalReason('programme.write')}
            aria-describedby={can('programme.write') ? undefined : AUTHOR_REFUSAL_ID}
            onClick={() => newDraft(active?.id)}
            data-action="tprm.approval-matrix.a02"
          >
            {drafting ? 'Creating' : versions.length === 0 ? 'Draft the first version' : 'Draft the next version'}
          </button>
        </div>
      </div>

      {/* The authoring buttons point at this line, so it keeps the refusal's
          words for a screen reader while the screen shows a label and an (i). */}
      {!can('programme.write') && (
        <p className="muted sw-row" id={AUTHOR_REFUSAL_ID}>
          <Status tone="neutral">Read only</Status>
          <span className="vh">{refusalReason('programme.write')}</span>
          <ReasonTip label="Why you cannot change the matrix" text={refusalReason('programme.write')} />
          <span>You can read it because you can read the vendors it governs</span>
          <Toggletip label="Why the matrix is readable">
            Whoever is asked to sign a stage should be able to see the rule that asked them.
          </Toggletip>
        </p>
      )}

      {draftError && <ErrorBox error={draftError} what="The draft" verb="created" />}

      {release && (
        <ReleaseRulesBanner
          draft={release}
          step={releaseStep(
            release,
            {
              subject,
              isTPRMAdmin: signedInRoleCodes().some((c) => c.trim().toLowerCase() === 'tprm_owner'),
              canApprove: can('vendor.approve'),
            },
            releaseEditedBy,
            shortDate,
          )}
          busy={releaseBusy}
          onAct={() => releaseAct(release.id)}
          onOpen={() => setOpenId(release.id)}
          isOpen={openId === release.id}
          activeVersionNo={active?.versionNo}
        />
      )}
      {releaseError && <ErrorBox error={releaseError} what="The activation" verb="recorded" />}
      {releaseNote && (
        <div className="ok-note sw-row" role="status">
          <span>{releaseNote}</span>
        </div>
      )}

      <div className="panel">
        <h2>Versions</h2>
        {versions.length === 0 ? (
          <div className="empty">
            <strong>No version has ever been promoted here</strong>
            No vendor can be activated until one is{' '}
            <Toggletip label="Why the gate refuses">
              The gate refuses rather than passes: defaulting to nobody would make it a formality on exactly the estates
              that skipped configuring it. A new draft starts from the shipped default.
            </Toggletip>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <caption className="vh">Versions of the approval matrix</caption>
              <thead>
                <tr>
                  <th scope="col">Version</th>
                  <th scope="col">Status</th>
                  <th scope="col">Content hash</th>
                  <th scope="col">Drafted by</th>
                  <th scope="col">Promoted by</th>
                  <th scope="col">
                    <span className="vh">Open</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {versions.map((v) => (
                  <tr key={v.id} aria-current={v.id === openId ? 'true' : undefined}>
                    <th scope="row">v{v.versionNo}</th>
                    <td>
                      <VersionStatus status={v.status} />
                      {v.status === 'DRAFT' && v.proposedBy && (
                        <span className="cell-sub">Proposed by {v.proposedBy}</span>
                      )}
                    </td>
                    <td>
                      {v.contentSha256 ? (
                        <span className="mono" title={v.contentSha256}>
                          {v.contentSha256.slice(0, 12)}
                        </span>
                      ) : (
                        <span className="muted">not stamped until promoted</span>
                      )}
                    </td>
                    <td>
                      {/* The upgrade is not a person, so it is named in words
                          rather than by the marker it writes as. */}
                      {v.fromRelease ? <span>Added by this release</span> : <span className="mono">{v.createdBy}</span>}
                      <span className="cell-sub">{shortDate(v.createdAt)}</span>
                    </td>
                    <td>
                      {v.activatedBy ? (
                        <>
                          <span className="mono">{v.activatedBy}</span>
                          <span className="cell-sub">{v.activatedAt ? shortDate(v.activatedAt) : ''}</span>
                        </>
                      ) : (
                        <span className="muted">never</span>
                      )}
                    </td>
                    <td>
                      <button type="button" onClick={() => setOpenId(v.id)}>
                        {v.id === openId ? 'Open' : 'View'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {versionError && <ErrorBox error={versionError} what="That version of the matrix" verb="read" />}
      {openId && !version && !versionError && <Loading what="the version" />}

      {version && draft && (
        <>
          <div className="studio-bar" role="region" aria-label="Version actions">
            <span className="sw-row">
              Version {version.versionNo} <VersionStatus status={version.status} />
            </span>
            {version.status === 'DRAFT' ? (
              dirty ? (
                <Status tone="warn">Unsaved</Status>
              ) : (
                <Status tone="plain">All saved</Status>
              )
            ) : (
              <>
                <Status tone="plain">Frozen</Status>
                <span className="muted">Draft the next version to change anything</span>
              </>
            )}
            <span className="studio-spacer" />
            <button type="button" onClick={() => openVersion(version.id)} disabled={checking}>
              {checking ? 'Reading' : 'Check the stored version'}
            </button>
            {version.status === 'DRAFT' && (
              <button
                type="button"
                className="primary"
                onClick={save}
                disabled={saving || !can('programme.write')}
                title={can('programme.write') ? undefined : refusalReason('programme.write')}
                aria-describedby={can('programme.write') ? undefined : AUTHOR_REFUSAL_ID}
                data-action="tprm.approval-matrix.a01"
              >
                {saving ? 'Saving' : 'Save draft'}
              </button>
            )}
          </div>

          {saveError && <ErrorBox error={saveError} what="The draft" verb="saved" />}
          {/* The API's note says what a save does and does not do. It is kept
              word for word behind the (i) beside the confirmation. */}
          {savedNote && (
            <div className="ok-note sw-row" role="status">
              <strong>Saved</strong>
              <ReasonTip label="What saving did" text={savedNote} />
            </div>
          )}

          {checked && <ValidationPanel checked={checked} dirty={dirty} />}

          {draft.contractWaiver === true && (
            <p className="muted sw-row">
              <span>Under these rules an approved contract waiver can stand in for a missing contract, for at most 90 days</span>
              <Toggletip label="Who approves a contract waiver">
                The TPRM lead and the vendor's business owner approve it, and never the person who asked for it. It shows on the
                vendor page until the contract is filed.
              </Toggletip>
            </p>
          )}

          {/* A draft the release added is put in force by two people from the
              panel at the top, never by this single button. */}
          {version.status === 'DRAFT' && version.fromRelease && (
            <section className="panel">
              <h2>Activate</h2>
              <p className="muted">
                These rules came with this release. Activate them from the panel at the top of this page: the TPRM Admin
                proposes the activation and a second, different person confirms it.
              </p>
            </section>
          )}
          {version.status === 'DRAFT' && !version.fromRelease && checked?.valid && (
            <PromotePanel
              promoting={promoting}
              dirty={dirty}
              drafted={drafted}
              edited={edited}
              onPromote={promote}
              activeVersionNo={active?.versionNo}
            />
          )}
          {promoteError && <ErrorBox error={promoteError} what="The promotion" verb="completed" />}
          {promoted && (
            <div className="ok-note sw-row" role="status">
              <strong>
                Version {promoted.versionNo} is in force
                {promoted.contentSha256 ? ', hash ' + promoted.contentSha256.slice(0, 12) : ''}
              </strong>
              <ReasonTip label="What promotion changed" text={promoted.note} />
            </div>
          )}

          {ordered.length === 0 ? (
            <Loading what="the tier table" />
          ) : (
            <div className="studio">
              {/* A tier that is not on the page still says when something in
                  it would stop the matrix being accepted: behind a tab, a
                  problem nobody scrolls past is a problem nobody sees. */}
              <PageTabs
                label="Parts of the approval matrix"
                value={openPart}
                onChange={setPart}
                tabs={[
                  ...ordered.map((t): PageTab<string> => {
                    const wrong = tierProblemCount(draft, t.tier, bands, vocabulary)
                    return {
                      key: 'tier-' + t.tier,
                      label: t.label || tierLabel(t.tier),
                      ...(wrong > 0 ? { alert: wrong + (wrong === 1 ? ' thing to fix' : ' things to fix') } : {}),
                    }
                  }),
                  ((): PageTab<string> => {
                    const wrong = exitProblemCount((draft as MatrixDocument & { exit?: ExitRowDoc[] }).exit, ordered)
                    return {
                      key: 'exit',
                      label: 'Signing a vendor out',
                      ...(wrong > 0 ? { alert: wrong + (wrong === 1 ? ' thing to fix' : ' things to fix') } : {}),
                    }
                  })(),
                ]}
              >
                {ordered
                  .filter((t) => 'tier-' + t.tier === openPart)
                  .map((t) => (
                    <TierSection
                      key={t.tier}
                      tier={t}
                      doc={draft}
                      bands={bands}
                      vocabulary={vocabulary}
                      editable={!!editable}
                      onChange={set}
                    />
                  ))}
                {/* Who signs a supplier out, in the same document and under the
                    same promotion as who signs it in. */}
                {openPart === 'exit' && (
                  <ExitChainEditor
                    rows={(draft as MatrixDocument & { exit?: ExitRowDoc[] }).exit}
                    tiers={ordered}
                    vocabulary={exitVocabulary}
                    editable={!!editable}
                    onChange={(rows) => set({ ...draft, exit: rows } as MatrixDocument)}
                  />
                )}
              </PageTabs>
            </div>
          )}
        </>
      )}
    </>
  )
}

function TierSection({
  tier,
  doc,
  bands,
  vocabulary,
  editable,
  onChange,
}: {
  tier: TierRow
  doc: MatrixDocument
  bands: string[]
  vocabulary: GateConditionEntry[]
  editable: boolean
  onChange: (next: MatrixDocument) => void
}) {
  const held = doc.conditions.find((c) => c.tier === tier.tier)
  const inForce = new Set(held?.conditions ?? [])
  const label = tier.label || tierLabel(tier.tier)
  const whyId = 'tier-why-' + tier.tier

  function toggle(code: string) {
    const next = new Set(inForce)
    if (next.has(code)) next.delete(code)
    else next.add(code)
    // The catalogue order is the order a person works through them: classify,
    // assess, clear what the assessment found, paper it, sign it. Storing them
    // in click order would make two identical documents hash differently.
    const ordered = vocabulary.map((v) => v.code).filter((c) => next.has(c))
    const stray = [...next].filter((c) => !ordered.includes(c))
    onChange(withTier(doc, tier.tier, { conditions: [...ordered, ...stray] }))
  }

  const shown =
    vocabulary.length > 0
      ? vocabulary
      : (held?.conditions ?? []).map((c) => ({ code: c, name: c, unmet: '', why: '' }))

  return (
    <section className="panel" aria-labelledby={'tier-' + tier.tier + '-heading'}>
      {/* What puts a vendor in this tier is the tier table's own sentence, so
          it is kept whole, one press from the heading it explains. */}
      <h2 id={'tier-' + tier.tier + '-heading'} className="sw-title">
        {label}
        <ReasonTip label={'What puts a vendor in ' + label} text={tier.why} />
      </h2>

      <fieldset className="fieldset">
        <legend>What must be true before a vendor here is activated</legend>
        <div className="colgrid">
          {shown.map((c) => {
            const on = inForce.has(c.code)
            const essential = ESSENTIAL[c.code]
            const id = 'cond-' + tier.tier + '-' + c.code
            return (
              <div key={c.code} className="check">
                <input
                  id={id}
                  type="checkbox"
                  checked={on}
                  disabled={!editable}
                  aria-describedby={id + '-why'}
                  onChange={() => toggle(c.code)}
                />
                <div>
                  {/* The catalogue's reasons run to five hundred characters,
                      too long for a cell of this grid, so they open from the
                      (i). The hidden copy keeps them as the box's description. */}
                  <label htmlFor={id}>
                    <strong>{c.name}</strong>
                  </label>{' '}
                  <ReasonTip label={'Why ' + c.name.toLowerCase()} text={c.why || c.code} />
                  <span className="vh" id={id + '-why'}>
                    {c.why || c.code}
                  </span>
                  {!on && essential && (
                    <p className="field-error">
                      {essential.line}{' '}
                      <Toggletip label={'Why ' + c.name.toLowerCase() + ' is required'}>{essential.tip}</Toggletip>
                    </p>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        <div className="field" style={{ marginTop: 10 }}>
          <label htmlFor={whyId}>Why this tier is gated the way it is</label>
          <textarea
            id={whyId}
            rows={2}
            disabled={!editable}
            value={held?.why ?? ''}
            onChange={(e) => onChange(withTier(doc, tier.tier, { why: e.target.value }))}
            aria-describedby={whyId + '-note'}
          />
        </div>
      </fieldset>

      {/* Side by side where there is room: four chains one under another was
          a screen and a half for each tier. */}
      <div className="gate-grid">
        {bands.map((band) => (
          <ChainEditor key={band} band={band} tier={tier.tier} doc={doc} editable={editable} onChange={onChange} />
        ))}
      </div>
    </section>
  )
}

// How many things in one tier would stop the matrix being accepted, counted
// the way the tier's own section shows them: a required condition switched
// off, and each problem in each band's chain.
export function tierProblemCount(doc: MatrixDocument, tier: number, bands: string[], vocabulary: GateConditionEntry[]): number {
  const inForce = new Set(doc.conditions.find((c) => c.tier === tier)?.conditions ?? [])
  const required = vocabulary.filter((v) => ESSENTIAL[v.code] && !inForce.has(v.code)).length
  return required + bands.reduce((n, band) => n + chainProblems(rowFor(doc, band, tier)).length, 0)
}

function ChainEditor({
  band,
  tier,
  doc,
  editable,
  onChange,
}: {
  band: string
  tier: number
  doc: MatrixDocument
  editable: boolean
  onChange: (next: MatrixDocument) => void
}) {
  const row = rowFor(doc, band, tier)
  const stages = row?.stages ?? []
  const problems = chainProblems(row)
  const options = capabilityCatalogue().filter((c) => rolesWith(c.code).length > 0)
  const acts = options.map((c) => ({ value: c.code, label: c.act }))
  const base = 'chain-' + tier + '-' + band

  function put(next: ApprovalStage[]) {
    onChange(withStages(doc, band, tier, next))
  }

  function move(from: number, to: number) {
    if (to < 0 || to >= stages.length) return
    const next = [...stages]
    const a = next[from]
    const b = next[to]
    if (!a || !b) return
    next[from] = b
    next[to] = a
    put(next)
  }

  return (
    <div className="gate-card">
      <div className="gate-head">
        <BandChip band={band} />
        <span className="muted">
          {stages.length === 0
            ? 'no signature'
            : stages.length + (stages.length === 1 ? ' signature' : ' signatures, in order')}
        </span>
        {editable && (
          <button
            type="button"
            onClick={() => put([...stages, { capability: NEW_STAGE, why: '' }])}
          >
            Add a signature
          </button>
        )}
      </div>

      {/* The (i) sits outside the label so its words never become part of the
          checkbox's name. */}
      <div className="check">
        <input
          id={base + '-accept'}
          type="checkbox"
          disabled={!editable}
          checked={row?.requiresAcceptance === true}
          onChange={(e) => onChange(withAcceptance(doc, band, tier, e.target.checked))}
        />
        <div>
          <label htmlFor={base + '-accept'}>
            A vendor in this band needs an approved risk acceptance to be activated
          </label>{' '}
          <Toggletip label="Why the band alone can ask for one">
            Section 14 opens with the score rather than with a single answer, so this is what asks for one when the band
            alone is the reason.
          </Toggletip>
        </div>
      </div>

      {stages.length > 0 && (
        <ol className="act-chain">
          {stages.map((s, i) => {
            const holders = holdersOf(s.capability)
            const capId = base + '-cap-' + i
            const nameId = base + '-name-' + i
            const whyId = base + '-why-' + i
            return (
              <li key={i}>
                <div className="form-row">
                  <div className="field">
                    <label htmlFor={capId}>Signature {i + 1}</label>
                    {/* Thirty-two acts, on every signature of every chain. A
                        stage always names one, so there is nothing to clear.
                        An act no role holds any more is still listed for the
                        stage that names it, so the stage says what it asks.
                        394 px is what the select took, sized by its longest
                        act: the two fields after it are laid out to start on
                        the next line. */}
                    <Picker
                      bare
                      id={capId}
                      label={'Signature ' + (i + 1)}
                      width={394}
                      disabled={!editable}
                      clearable={false}
                      value={s.capability}
                      describedBy={capId + '-note'}
                      options={
                        options.some((c) => c.code === s.capability)
                          ? acts
                          : [{ value: s.capability, label: capabilityAct(s.capability) }, ...acts]
                      }
                      placeholder="Search the acts"
                      noun="act"
                      onChange={(code) => {
                        const next = code as Capability
                        const holders = capabilityCatalogue().length > 0 ? rolesWith(next) : null
                        put(stages.map((x, j) => (j === i ? withStageCapability(x, next, holders) : x)))
                      }}
                    />
                    {/* The list of roles holding it is long, the same on most
                        rows, and printed once per stage it was most of the
                        screen, so it sits behind the (i). */}
                    <p className="field-note" id={capId + '-note'}>
                      {holders ? (
                        <Why label="Who holds it">{holders}</Why>
                      ) : (
                        <span>No role holds this.</span>
                      )}
                    </p>
                    {/* Who may sign it, where the stage is one office's. Read
                        only here, and kept on save with the rest of the stage. */}
                    {s.roles && s.roles.length > 0 && (
                      <p className="field-note">Signed only by: {roleNames(s.roles).join(' or ')}</p>
                    )}
                  </div>
                  <div className="field">
                    <label htmlFor={nameId}>Name on screen</label>
                    <input
                      id={nameId}
                      type="text"
                      disabled={!editable}
                      value={s.name ?? ''}
                      placeholder="For example TPRM approval"
                      onChange={(e) => put(stages.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
                    />
                  </div>
                  <div className="field studio-grow">
                    <label htmlFor={whyId}>What this signature is for</label>
                    <input
                      id={whyId}
                      type="text"
                      disabled={!editable}
                      value={s.why}
                      aria-invalid={s.why.trim() === '' ? true : undefined}
                      aria-describedby={whyId + '-note'}
                      onChange={(e) => put(stages.map((x, j) => (j === i ? { ...x, why: e.target.value } : x)))}
                    />
                  </div>
                </div>
                {editable && (
                  <div className="chiprow">
                    <button type="button" disabled={i === 0} onClick={() => move(i, i - 1)}>
                      Move earlier
                    </button>
                    <button type="button" disabled={i === stages.length - 1} onClick={() => move(i, i + 1)}>
                      Move later
                    </button>
                    <button type="button" onClick={() => put(stages.filter((_, j) => j !== i))}>
                      Remove
                    </button>
                  </div>
                )}
              </li>
            )
          })}
        </ol>
      )}

      {problems.length > 0 && (
        <ul className="issue-list">
          {problems.map((p, i) => (
            <li key={i}>{p}</li>
          ))}
        </ul>
      )}
    </div>
  )
}

function ValidationPanel({ checked, dirty }: { checked: Checked; dirty: boolean }) {
  return (
    <div className={checked.valid ? 'ok-note' : 'issue-list'} role={checked.valid ? 'status' : 'alert'}>
      <h3>
        {checked.valid
          ? 'This matrix would be accepted'
          : checked.problems.length + (checked.problems.length === 1 ? ' problem' : ' problems') + ' would stop this being promoted'}
      </h3>
      {checked.problems.length > 0 && (
        <ul>
          {checked.problems.map((p, i) => (
            <li key={i}>{p}</li>
          ))}
        </ul>
      )}
      <p className="muted sw-row">
        <span>Checked against {checked.of}</span>
        {dirty && (
          <>
            <Status tone="warn">Unchecked changes</Status>
            <span>Save them to check them</span>
          </>
        )}
      </p>
    </div>
  )
}

function PromotePanel({
  promoting,
  dirty,
  drafted,
  edited,
  onPromote,
  activeVersionNo,
}: {
  promoting: boolean
  dirty: boolean
  drafted: boolean
  // The reader changed this draft after it was written, which makes them one
  // of its authors (release 3.2): the server refuses them as it refuses the
  // person who drafted it.
  edited: boolean
  onPromote: () => void
  activeVersionNo?: number
}) {
  // vendor.approve, not programme.write. Changing who has to sign a supplier in is
  // a decision of the same kind as signing one in, and the route says so.
  const allowed = can('vendor.approve')
  const [about, setAbout] = useState(false)
  const held = !allowed
    ? refusalReason('vendor.approve')
    : drafted
      ? 'You drafted this version, and a change to who must approve a supplier needs a second person to agree to it. Ask somebody else to promote it.'
      : edited
        ? 'You changed this draft, and a change to who must approve a supplier needs a second person who did not write it. Ask somebody else to promote it.'
        : dirty
          ? 'Save the draft first. Promotion reads what is stored, not what is on screen.'
          : ''
  const retires = activeVersionNo ? 'version ' + activeVersionNo : 'whatever is in force'

  // The full sentence above stays the button's title and, through the hidden
  // copy below, its description. What the screen shows is the short form.
  const heldShown = !allowed ? (
    <>
      <Status tone="neutral">Read only</Status>
      <ReasonTip label="Why you cannot promote" text={held} />
    </>
  ) : drafted || edited ? (
    <>
      <span>
        {drafted
          ? 'You drafted this version, so ask somebody else to promote it'
          : 'You changed this draft, so ask somebody else to promote it'}
      </span>
      <Toggletip label="Why a second person">
        A change to who must approve a supplier needs a second person to agree to it.
      </Toggletip>
    </>
  ) : (
    <span>Save the draft first, promotion reads what is stored</span>
  )

  return (
    <section className="panel">
      <h2>Promote</h2>
      {/* What promotion keeps and what it asks to be signed again is more than
          a line, so the line says what it does and the drawer says the rest. */}
      <p className="muted sw-row">
        <span>Retires {retires} and stamps a content hash</span>
        <button type="button" className="linkish" onClick={() => setAbout(true)}>
          About promotion
        </button>
      </p>
      <Dialog shape="drawer" kind="record" open={about} onClose={() => setAbout(false)} title="About promotion">
        <div className="sw-stack">
          <div>
            <h3>The content hash</h3>
            <p>
              Promotion retires {retires} and stamps a content hash, so the question of which rule a vendor was approved
              under has an answer years later.
            </p>
          </div>
          <div>
            <h3>Vendors already waiting</h3>
            <p>
              A vendor already waiting for approval finishes under the rules it was sent under, with the signatures it
              already has. The new version applies to every vendor sent for approval after it is in force, because a
              signature is agreement to the rule that was in force at the time.
            </p>
          </div>
        </div>
      </Dialog>
      <div className="commit-bar">
        <button
          type="button"
          className="primary"
          onClick={onPromote}
          disabled={promoting || held !== ''}
          title={held || undefined}
          aria-describedby={held ? 'promote-held' : undefined}
          data-action="tprm.approval-matrix.a03"
        >
          {promoting ? 'Promoting' : 'Promote this version'}
        </button>
        {held && (
          <span className="muted sw-row">
            <span className="vh" id="promote-held">
              {held}
            </span>
            {heldShown}
          </span>
        )}
      </div>
    </section>
  )
}

// The approval rules a release added to this tenant as a draft (release 3.2,
// review H3), what they change in plain words, and the two acts that put them
// in force: the TPRM Admin proposes, a second person confirms. The words come
// from lib/matrixDraft.ts, where a test holds them; the server decides both
// acts and refuses the same people the buttons are greyed for.
function ReleaseRulesBanner({
  draft,
  step,
  busy,
  onAct,
  onOpen,
  isOpen,
  activeVersionNo,
}: {
  draft: ApprovalMatrixVersion
  step: ReleaseStep
  busy: boolean
  onAct: () => void
  onOpen: () => void
  isOpen: boolean
  activeVersionNo?: number
}) {
  const proposed = !!draft.proposedBy
  return (
    <section className="panel" role="region" aria-labelledby="release-rules-heading">
      <h2 id="release-rules-heading">{RELEASE_RULE_HEADLINE}</h2>
      <p>{RELEASE_RULE_INTRO}</p>
      <h3>Who approves a vendor under the new rules</h3>
      <ul>
        {RELEASE_RULE_LINES.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      <h3>What stays safe</h3>
      <ul>
        {RELEASE_RULE_SAFEGUARDS.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      <p className="muted">
        {activeVersionNo ? 'The rules in force now are version ' + activeVersionNo + '. ' : ''}
        The new rules are draft version {draft.versionNo}.
      </p>
      <p className="sw-row" role="status">
        <Status tone={proposed ? 'info' : 'neutral'}>{proposed ? 'Proposed' : 'Not proposed'}</Status>
        <span>{step.standing}</span>
      </p>
      <div className="commit-bar">
        {!proposed ? (
          <button
            type="button"
            className="primary"
            onClick={onAct}
            disabled={busy || !step.propose.allowed}
            title={step.propose.why || undefined}
            aria-describedby={step.propose.why ? 'release-propose-held' : undefined}
          >
            {busy ? 'Proposing' : 'Propose activation'}
          </button>
        ) : (
          <button
            type="button"
            className="primary"
            onClick={onAct}
            disabled={busy || !step.confirm.allowed}
            title={step.confirm.why || undefined}
            aria-describedby={step.confirm.why ? 'release-confirm-held' : undefined}
          >
            {busy ? 'Confirming' : 'Confirm activation'}
          </button>
        )}
        {!isOpen && (
          <button type="button" onClick={onOpen}>
            Read the new rules in full
          </button>
        )}
        {!proposed && step.propose.why && (
          <span className="muted" id="release-propose-held">
            {step.propose.why}
          </span>
        )}
        {proposed && step.confirm.why && (
          <span className="muted" id="release-confirm-held">
            {step.confirm.why}
          </span>
        )}
      </div>
    </section>
  )
}
